import { useEffect, useState } from 'react'
import { refEqual, queryEqual } from 'firebase/firestore'
import type { DocumentReference, Query, DocumentData } from 'firebase/firestore'
import { onSnapshot, getCachedSnapshot } from '../lib/firestore'
import { classifyError, type ClassifiedError } from '../lib/errors'

function docRefSame<T extends DocumentData>(
  a: DocumentReference<T> | null,
  b: DocumentReference<T> | null,
): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return refEqual(a, b)
}

function querySame<T extends DocumentData>(a: Query<T> | null, b: Query<T> | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return queryEqual(a, b)
}

const EMPTY_ARRAY: never[] = []

// `loading` means "no usable data to render yet." When the in-memory
// subscription cache already has data, loading is false immediately so
// consumers show cached content instead of skeletons — critical for
// festival-goers on spotty cell connections where Firestore's persistent
// cache has data but the network round-trip is slow.

function readCachedDoc<T extends DocumentData>(
  ref: DocumentReference<T> | null,
): { data: T | null; loading: boolean } {
  if (!ref) return { data: null, loading: false }
  const snap = getCachedSnapshot(ref)
  if (!snap) return { data: null, loading: true }
  return {
    data: snap.exists() ? ({ id: snap.id, ...snap.data() } as unknown as T) : null,
    loading: false,
  }
}

function readCachedCollection<T extends DocumentData>(
  ref: Query<T> | null,
): { data: T[]; loading: boolean; fromCache: boolean } {
  if (!ref) return { data: EMPTY_ARRAY, loading: false, fromCache: false }
  const snap = getCachedSnapshot(ref)
  if (!snap) return { data: EMPTY_ARRAY, loading: true, fromCache: true }
  return {
    data: snap.docs.map(d => ({ id: d.id, ...d.data() }) as unknown as T),
    loading: false,
    fromCache: snap.metadata.fromCache,
  }
}

export function useFirestoreDoc<T extends DocumentData>(
  ref: DocumentReference<T> | null,
): { data: T | null; loading: boolean; error: ClassifiedError | null } {
  const [trackedRef, setTrackedRef] = useState<DocumentReference<T> | null>(ref)
  const [data, setData] = useState<T | null>(() => readCachedDoc(ref).data)
  const [loading, setLoading] = useState(() => readCachedDoc(ref).loading)
  const [error, setError] = useState<ClassifiedError | null>(null)

  // The reset below re-renders, but this pass still runs to completion, so it
  // returns the new ref's values rather than the previous ref's stale state.
  let current = { data, loading, error }
  if (!docRefSame(trackedRef, ref)) {
    setTrackedRef(ref)
    const cached = readCachedDoc(ref)
    setData(cached.data)
    setLoading(cached.loading)
    setError(null)
    current = { ...cached, error: null }
  }

  useEffect(() => {
    if (!trackedRef) return
    return onSnapshot(
      trackedRef,
      snap => {
        setData(snap.exists() ? ({ id: snap.id, ...snap.data() } as unknown as T) : null)
        setLoading(false)
      },
      err => {
        setError(classifyError(err))
        setLoading(false)
      },
    )
  }, [trackedRef])

  return { data: current.data, loading: current.loading && !current.data, error: current.error }
}

export function useFirestoreCollection<T extends DocumentData>(
  ref: Query<T> | null,
): { data: T[]; loading: boolean; fromCache: boolean; error: ClassifiedError | null } {
  const [trackedRef, setTrackedRef] = useState<Query<T> | null>(ref)
  const [data, setData] = useState<T[]>(() => readCachedCollection(ref).data)
  const [loading, setLoading] = useState(() => readCachedCollection(ref).loading)
  const [fromCache, setFromCache] = useState(() => readCachedCollection(ref).fromCache)
  const [error, setError] = useState<ClassifiedError | null>(null)

  let current = { data, loading, fromCache, error }
  if (!querySame(trackedRef, ref)) {
    setTrackedRef(ref)
    const cached = readCachedCollection(ref)
    setData(cached.data)
    setLoading(cached.loading)
    setFromCache(cached.fromCache)
    setError(null)
    current = { ...cached, error: null }
  }

  useEffect(() => {
    if (!trackedRef) return
    return onSnapshot(
      trackedRef,
      snap => {
        setData(snap.docs.map(d => ({ id: d.id, ...d.data() }) as unknown as T))
        setLoading(false)
        setFromCache(snap.metadata.fromCache)
      },
      err => {
        setError(classifyError(err))
        setLoading(false)
        setFromCache(false)
      },
    )
  }, [trackedRef])

  return {
    data: current.data,
    loading: current.loading && current.data.length === 0,
    /** The rows came from the local cache and the server hasn't confirmed them — they may be a partial result. */
    fromCache: current.fromCache,
    error: current.error,
  }
}
